import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { parseISO, differenceInDays } from 'date-fns';

const PerformanceKPIs = ({ bookings, pipelines }) => {
  const [selectedMetric, setSelectedMetric] = useState(null);

  // Calculate average deal size
  const avgDealSize = pipelines?.length > 0
    ? pipelines?.reduce((sum, p) => sum + parseFloat(p?.estimatedValue || 0), 0) / pipelines?.length
    : 0;

  // Calculate average sales cycle
  const completedDeals = pipelines?.filter(p => p?.currentStage === 'closed_won');
  const avgSalesCycle = completedDeals?.length > 0
    ? completedDeals?.reduce((sum, p) => {
        const booking = bookings?.find(b => b?.id === p?.bookingId);
        if (booking && p?.updatedAt) {
          return sum + differenceInDays(parseISO(p?.updatedAt), parseISO(booking?.createdAt));
        }
        return sum;
      }, 0) / completedDeals?.length
    : 0;

  // Calculate consultation-to-proposal rate
  const completedConsultations = bookings?.filter(b => b?.status === 'completed')?.length || 0;
  const proposalsSent = pipelines?.filter(p => 
    ['proposal', 'negotiation', 'closed_won']?.includes(p?.currentStage)
  )?.length || 0;
  const consultationToProposal = completedConsultations > 0
    ? ((proposalsSent / completedConsultations) * 100)?.toFixed(1)
    : 0;

  // Calculate proposal-to-close rate
  const dealsWon = pipelines?.filter(p => p?.currentStage === 'closed_won')?.length || 0;
  const proposalToClose = proposalsSent > 0
    ? ((dealsWon / proposalsSent) * 100)?.toFixed(1)
    : 0;

  const kpis = [
    {
      label: 'Avg Deal Size',
      value: `$${avgDealSize?.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
      icon: 'DollarSign',
      color: 'bg-green-50',
      iconColor: 'text-green-600',
      description: 'Average estimated value per deal',
      details: `Based on ${pipelines?.length || 0} total deals in pipeline`
    },
    {
      label: 'Avg Sales Cycle',
      value: `${avgSalesCycle?.toFixed(0)} days`,
      icon: 'Clock',
      color: 'bg-[#E6F0F5]',
      iconColor: 'text-[#002B45]',
      description: 'Time from consultation to close',
      details: `Based on ${completedDeals?.length || 0} closed deals`
    },
    {
      label: 'Consultation → Proposal',
      value: `${consultationToProposal}%`,
      icon: 'ArrowRight',
      color: 'bg-[#D1F5F0]',
      iconColor: 'text-[#1FB5A5]',
      description: 'Conversion rate to proposal stage',
      details: `${proposalsSent} proposals from ${completedConsultations} consultations`
    },
    {
      label: 'Proposal → Close',
      value: `${proposalToClose}%`,
      icon: 'CheckCircle',
      color: 'bg-purple-50',
      iconColor: 'text-purple-600',
      description: 'Proposal win rate',
      details: `${dealsWon} deals won from ${proposalsSent} proposals`
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-elevation-1 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-foreground mb-1">Performance KPIs</h3>
          <p className="text-sm text-muted-foreground">Key performance indicators with drill-down</p>
        </div>
        <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
          <Icon name="BarChart3" size={20} className="text-amber-600" />
        </div>
      </div>

      <div className="space-y-4">
        {kpis?.map((kpi, index) => (
          <div 
            key={index}
            className="border border-gray-200 rounded-lg p-4 hover:border-[#1FB5A5] hover:shadow-md transition-all duration-200 cursor-pointer"
            onClick={() => setSelectedMetric(selectedMetric === index ? null : index)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${kpi?.color} rounded-lg flex items-center justify-center`}>
                  <Icon name={kpi?.icon} size={20} className={kpi?.iconColor} />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{kpi?.label}</p>
                  <p className="text-xl font-bold text-foreground">{kpi?.value}</p>
                </div>
              </div>
              <Icon 
                name={selectedMetric === index ? 'ChevronUp' : 'ChevronDown'} 
                size={20} 
                className="text-muted-foreground"
              />
            </div>
            
            {selectedMetric === index && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-muted-foreground mb-2">{kpi?.description}</p>
                <p className="text-xs text-gray-500">{kpi?.details}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-gradient-to-r from-[#D1F5F0] to-[#E6F0F5] rounded-lg border border-[#1FB5A5]/30">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Lightbulb" size={16} className="text-[#1FB5A5]" />
          <span className="text-sm font-medium text-foreground">Performance Tip</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Click on any KPI card to view detailed breakdown and insights. Monitor these metrics regularly to optimize your sales process.
        </p>
      </div>
    </div>
  );
};

export default PerformanceKPIs;